/**
 * JDBC URL parser → mssql / pgsql / mysql / oracle.
 *
 *   jdbc:sqlserver://host:1433;databaseName=db;user=u;password=…;encrypt=true;integratedSecurity=true  (parseJdbcSqlServer.ts)
 *   jdbc:postgresql://host:5432/db?user=u&password=…&ssl=true
 *   jdbc:mysql://host:3306/db?user=u&password=…
 *   jdbc:oracle:thin:@//host:1521/SERVICE   (service name)
 *   jdbc:oracle:thin:@host:1521:SID         (SID)
 */

import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import type { ParserMatch, ConnectionStringParser } from './types';
import { safeDecode } from './helpers';
import { parseJdbcSqlServer } from './parseJdbcSqlServer';

/** jdbc:{postgresql|mysql|mariadb}://host[:port]/db?params */
function parseNetworkUrl(rest: string, engine: 'pgsql' | 'mysql'): ParserMatch | null {
  let url: URL;
  try {
    // Reuse the http:// machinery for authority + path + query parsing.
    url = new URL(`http://${rest}`);
  } catch {
    return null;
  }

  const values: Partial<ConnectionFormValues> = { engine };
  const matchedFields: string[] = [];
  const unmatchedKeys: string[] = [];

  if (url.hostname) {
    values.server = url.hostname;
    matchedFields.push('server');
  }
  if (url.port) {
    values.port = url.port;
    matchedFields.push('port');
  }
  const database = url.pathname.replace(/^\//, '');
  if (database) {
    values.database = safeDecode(database);
    matchedFields.push('database');
  }

  const sslKeys = ['ssl', 'sslmode', 'usessl'];
  for (const [key, rawVal] of url.searchParams.entries()) {
    const lk = key.toLowerCase();
    if (lk === 'user' || lk === 'username') {
      values.login = safeDecode(rawVal);
      matchedFields.push('user');
    } else if (lk === 'password') {
      values.password = safeDecode(rawVal);
      matchedFields.push('password');
    } else if (sslKeys.includes(lk)) {
      const v = rawVal.toLowerCase();
      const sslOn = v !== 'disable' && v !== 'false' && v !== '0' && v !== 'none';
      values.ssl = sslOn;
      if (sslOn) matchedFields.push('SSL');
    } else {
      unmatchedKeys.push(key);
    }
  }

  return { format: 'jdbc', engine, values, matchedFields, unmatchedKeys };
}

/** jdbc:oracle:thin:@//host:port/SERVICE  or  @host:port:SID */
function parseOracle(rest: string): ParserMatch | null {
  // rest is everything after `jdbc:oracle:thin:` — expect a leading `@`.
  let body = rest.trim();
  if (!body.startsWith('@')) return null;
  body = body.slice(1);

  const values: Partial<ConnectionFormValues> = {
    engine: 'oracle',
    oracleConnectionStringType: 'ezconnect',
  };
  const matchedFields: string[] = [];

  // Service-name form: //host:port/SERVICE (port optional)
  const serviceMatch = /^\/\/([^:/]+)(?::(\d+))?\/(.+)$/.exec(body);
  if (serviceMatch) {
    values.server = serviceMatch[1];
    matchedFields.push('server');
    if (serviceMatch[2]) {
      values.port = serviceMatch[2];
      matchedFields.push('port');
    }
    values.oracleServiceName = serviceMatch[3];
    values.oracleUseSID = false;
    matchedFields.push('service name');
    return { format: 'jdbc', engine: 'oracle', values, matchedFields, unmatchedKeys: [] };
  }

  // SID form: host:port:SID
  const sidMatch = /^([^:/]+):(\d+):([^:/]+)$/.exec(body);
  if (sidMatch) {
    values.server = sidMatch[1];
    values.port = sidMatch[2];
    values.oracleServiceName = sidMatch[3];
    values.oracleUseSID = true;
    matchedFields.push('server', 'port', 'service name');
    return { format: 'jdbc', engine: 'oracle', values, matchedFields, unmatchedKeys: [] };
  }

  return null;
}

export const parseJdbc: ConnectionStringParser = (input) => {
  const trimmed = input.trim();
  if (!/^jdbc:/i.test(trimmed)) return null;
  const afterJdbc = trimmed.slice(5); // strip "jdbc:"
  const lower = afterJdbc.toLowerCase();

  if (lower.startsWith('sqlserver://')) {
    return parseJdbcSqlServer(afterJdbc.slice('sqlserver://'.length));
  }
  if (lower.startsWith('postgresql://') || lower.startsWith('postgres://')) {
    const prefix = lower.startsWith('postgresql://') ? 'postgresql://' : 'postgres://';
    return parseNetworkUrl(afterJdbc.slice(prefix.length), 'pgsql');
  }
  if (lower.startsWith('mysql://')) {
    return parseNetworkUrl(afterJdbc.slice('mysql://'.length), 'mysql');
  }
  if (lower.startsWith('mariadb://')) {
    return parseNetworkUrl(afterJdbc.slice('mariadb://'.length), 'mysql');
  }
  if (lower.startsWith('oracle:thin:')) {
    return parseOracle(afterJdbc.slice('oracle:thin:'.length));
  }

  return null;
};
